/**
 * MemberChecklistGrid — Spec 128: Replaces SearchableMultiSelect in ShareSheet
 *
 * Always-visible 2-column checkbox grid of org members.
 * No dropdown, no absolute positioning, no overflow issues.
 * Search input only appears when 8+ members.
 */

import React, { useState, useMemo } from 'react';
import type { SelectOption } from '../SearchableMultiSelect';

interface MemberChecklistGridProps {
  options: SelectOption[];
  selectedIds: number[];
  onChange: (ids: number[]) => void;
  disabled?: boolean;
}

const SEARCH_THRESHOLD = 8;

export const MemberChecklistGrid: React.FC<MemberChecklistGridProps> = ({
  options,
  selectedIds,
  onChange,
  disabled = false,
}) => {
  const [searchTerm, setSearchTerm] = useState('');

  const filteredOptions = useMemo(() => {
    if (!searchTerm.trim()) return options;
    const term = searchTerm.toLowerCase();
    return options.filter(o => o.label.toLowerCase().includes(term));
  }, [options, searchTerm]);

  const handleToggle = (id: number) => {
    if (disabled) return;
    if (selectedIds.includes(id)) {
      onChange(selectedIds.filter(sid => sid !== id));
    } else {
      onChange([...selectedIds, id]);
    }
  };

  return (
    <div className="flex flex-col gap-2">
      {options.length >= SEARCH_THRESHOLD && (
        <input
          type="text"
          className="w-full px-3 py-2 border border-border-light rounded text-[13px] text-text-primary bg-bg-primary box-border placeholder:text-text-tertiary focus:outline-none focus:border-accent dark:border-border-medium dark:bg-bg-secondary"
          placeholder="Search members..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          disabled={disabled}
        />
      )}

      <div className="grid gap-1.5 grid-cols-1 sm:grid-cols-2">
        {filteredOptions.map(option => {
          const isSelected = selectedIds.includes(option.id);
          return (
            <label
              key={option.id}
              className={`flex items-center gap-2 px-2.5 py-2 rounded cursor-pointer transition-colors duration-150 border border-transparent
                ${isSelected ? 'bg-[rgba(184,115,51,0.08)] border-[rgba(184,115,51,0.2)] dark:bg-[rgba(184,115,51,0.2)] dark:border-[rgba(184,115,51,0.35)]' : ''}
                ${isSelected ? 'hover:bg-[rgba(184,115,51,0.12)] dark:hover:bg-[rgba(184,115,51,0.25)]' : 'hover:bg-bg-secondary dark:hover:bg-bg-tertiary'}
                ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
            >
              <input
                type="checkbox"
                checked={isSelected}
                onChange={() => handleToggle(option.id)}
                disabled={disabled}
                className="w-4 h-4 shrink-0 accent-accent cursor-inherit"
              />
              <span className="text-[13px] font-medium text-text-primary overflow-hidden text-ellipsis whitespace-nowrap min-w-0">
                {option.label}
              </span>
            </label>
          );
        })}
      </div>

      {filteredOptions.length === 0 && searchTerm && (
        <p className="m-0 py-2 text-[13px] italic text-text-tertiary">No members match "{searchTerm}"</p>
      )}

      {selectedIds.length > 0 && (
        <div className="text-xs font-medium text-text-secondary pt-1">
          {selectedIds.length} selected
        </div>
      )}
    </div>
  );
};
